import { set, isObject } from 'lodash';

/**
 * Aggregates values from nested arrays of objects, applying weights to values and combining by keys
 * @param {Array<{weight?: number, values: Array<{k: string, v: number}>}>} data - Array of objects containing weights and value arrays
 * @param {boolean} [keepZeros=false] - Whether to include zero values in the output
 * @returns {Array<{k: string, v: number}>} Aggregated array of unique keys with their weighted totals
 *
 * @example
 * const data = [
 *   {
 *     weight: 0.5,
 *     values: [
 *       { k: 'growth', v: 0.4 },
 *       { k: 'value', v: 0.6 }
 *     ]
 *   },
 *   {
 *     weight: 0.3,
 *     values: [
 *       { k: 'growth', v: 0.3 },
 *       { k: 'blend', v: 0.7 }
 *     ]
 *   }
 * ];
 *
 * multiLevelAggregator(data);
 * // Returns: [
 * //   { k: 'growth', v: 0.29 },  // (0.4 * 0.5) + (0.3 * 0.3)
 * //   { k: 'value', v: 0.3 },    // (0.6 * 0.5)
 * //   { k: 'blend', v: 0.21 }    // (0.7 * 0.3)
 * // ]
 */
const multiLevelAggregator = (data, keepZeros=false) => {
  const aggregationMap = new Map();

  data.forEach(entry => {
      if (entry.weight===0&&!keepZeros) return;
    const weight = entry.weight || 1;

    entry.values.forEach(item => {
      const currentKey = item.k;
      if (!keepZeros && item.v === 0) return;
      const weightedValue = item.v * weight;
      if (aggregationMap.has(currentKey)) {
        aggregationMap.set(
          currentKey,
          aggregationMap.get(currentKey) + weightedValue
        );
      } else {
        aggregationMap.set(currentKey, weightedValue);
      }
    });
  });

  return Array.from(aggregationMap).map(([k, v]) => ({
    k,
    v: Number(v.toFixed(4))
  }));
};

/**
 * Converts a nested object into dot notation paths
 * @param {Object} obj - The object to convert
 * @param {string} [parentKey=''] - The parent key for nested objects
 * @param {Object} [result={}] - Accumulator for recursive calls
 * @param {boolean} [dropNulls=false] - Whether to exclude null/undefined values
 * @returns {Object} Object with flattened dot notation paths
 *
 * @example
 * const nested = {
 *   user: {
 *     details: {
 *       name: 'John',
 *       age: 30
 *     },
 *     settings: null
 *   }
 * };
 *
 * formatObjectAsDotpath(nested);
 * // Returns: {
 * //   'user.details.name': 'John',
 * //   'user.details.age': 30,
 * //   'user.settings': null
 * // }
 *
 * formatObjectAsDotpath(nested, '', {}, true);
 * // Returns: {
 * //   'user.details.name': 'John',
 * //   'user.details.age': 30
 * // }
 */
function formatObjectAsDotpath(obj, parentKey = '', result = {}, dropNulls = false) {
  Object.keys(obj).forEach(key => {
    const newKey = parentKey ? `${parentKey}.${key}` : key;
    if (obj[key] !== undefined && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      formatObjectAsDotpath(obj[key], newKey, result, dropNulls);
    } else {
      if (!dropNulls || obj[key]) {
        result[newKey] = obj[key];
      }
    }
  });
  return result;
}

/**
 * Creates a deep copy of an object
 * @param {Object} obj - Object to copy
 * @returns {Object} Deep copy of the input object
 *
 * @example
 * const original = { a: { b: 2 } };
 * const copy = deepCopy(original);
 * copy.a.b = 3;
 */
function deepCopy(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Converts a dot path object back into a nested object structure
 * @param {Object} dotPathObject - Object with dot notation paths
 * @returns {Object} Nested object structure
 *
 * @example
 * const flat = {
 *   'user.name': 'John',
 *   'user.details.age': 30
 * };
 *
 * deepObjectFromDotPath(flat);
 * // Returns: {
 * //   user: {
 * //     name: 'John',
 * //     details: {
 * //       age: 30
 * //     }
 * //   }
 * // }
 */
function deepObjectFromDotPath(dotPathObject) {
  const result = {};
  Object.keys(dotPathObject).forEach(dotPath => {
    set(result, dotPath, dotPathObject[dotPath]);
  });
  return result;
}

/**
 * Recursively updates a target object with values from an updates object
 * @param {Object} target - Object to update
 * @param {Object} updates - Object containing updates to apply
 *
 * @example
 * const target = {
 *   user: {
 *     name: 'John',
 *     settings: { theme: 'dark' }
 *   }
 * };
 *
 * const updates = {
 *   user: {
 *     settings: { theme: 'light' },
 *     age: 31
 *   }
 * };
 *
 * deepUpdate(target, updates);
 * // target becomes: {
 * //   user: {
 * //     name: 'John',
 * //     settings: { theme: 'light' },
 * //     age: 31
 * //   }
 * // }
 */
function deepUpdate(target, updates) {
  Object.keys(updates).forEach(key => {
    if (isObject(updates[key]) && !Array.isArray(updates[key])) {
      if (!target[key]) target[key] = {};
      deepUpdate(target[key], updates[key]);
    } else {
      set(target, key, updates[key]);
    }
  });
}

/**
 * Converts a nested object into dot notation paths
 * @param {Object} obj - Object to unwind
 * @param {string} [path=''] - Current path for recursive calls
 * @param {Object} [result={}] - Accumulator for recursive calls
 * @returns {Object} Flattened object with dot notation paths
 *
 * @example
 * const nested = {
 *   user: {
 *     details: {
 *       name: 'John',
 *       age: [25, 26, 27]
 *     }
 *   }
 * };
 *
 * unwindToDotpath(nested);
 * // Returns: {
 * //   'user.details.name': 'John',
 * //   'user.details.age': [25, 26, 27]
 * // }
 */
function unwindToDotpath(obj, path = '', result = {}) {
  Object.keys(obj).forEach(key => {
    const fullPath = path ? `${path}.${key}` : key;
    if (obj[key] !== null && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
      unwindToDotpath(obj[key], fullPath, result);
    } else {
      result[fullPath] = obj[key];
    }
  });
  return result;
}

/**
 * Adds a prefix to all keys in an object
 * @param {Object} obj - Object whose keys need prefixing
 * @param {string} prefix - Prefix to add to each key
 * @returns {Object} New object with prefixed keys
 *
 * @example
 * const obj = {
 *   name: 'John',
 *   age: 30
 * };
 *
 * prefixObjectKeys(obj, 'user');
 * // Returns: {
 * //   'user.name': 'John',
 * //   'user.age': 30
 * // }
 */
function prefixObjectKeys(obj, prefix) {
  let newObj = {};
  Object.keys(obj).forEach(key => {
    newObj[`${prefix}.${key}`] = obj[key];
  });
  return newObj;
}

export {
  deepCopy,
  formatObjectAsDotpath,
  deepUpdate,
  deepObjectFromDotPath,
  unwindToDotpath,
  prefixObjectKeys,
  multiLevelAggregator
};